import React, { FC, useEffect } from 'react';
import { Button, Card, Table, Space, Modal, Form, Col, Row, Input, Select, Tabs, Divider } from 'antd';
// import { useSelector } from 'react-redux';
import { LeftOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import SchoolMap from 'components/SchoolMap';

import { AppDispatch, CombinedStates } from '../../core/types';
import Main from '../../components/layout/Main';
import { updateSchool } from '../../core/school/schoolApi';
import { loadSingleSchool, resetSchoolCreated } from '../../core/school/schoolSlice';
import { FinishedFailedProps, valuesProps } from './types';
import { School } from '../../core/school/types';
import { schoolTypeOptions } from '../../core/utils/helpers';
import SchoolIndicators from '../../components/SchoolIndicators';
import SchoolContacts from '../../components/SchoolContacts/index';

import './schoolEdit.scss';

type SchoolFormValues = Omit<School, 'id'>;

const { TabPane } = Tabs;
const SchoolEdit = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const location = useLocation();
    const defaultActiveKey = location.state?.activeTab || 'main';

    const {
        singleSchool,
        isFetching,
    } = useSelector((state:CombinedStates) => state.school);

    const {
        userData,
    } = useSelector((state:CombinedStates) => state.user);

    const [form] = Form.useForm();

    useEffect(() => {
        dispatch(resetSchoolCreated());
    }, [dispatch]);

    const onFinish = (values: Omit<School, 'id'>) => {
        if (!singleSchool?.id) return;
        dispatch(updateSchool({ ...values, id: singleSchool?.id }));
    };

    const onFinishFailed:FC<FinishedFailedProps> = ({ errorFields }) => {
        form.scrollToField(errorFields[0]?.name);
        return null;
    };

    const toolbar = [{ label: 'Item 1', link: '/abc', iconName: 'home' }];

    const handleBack = () => {
        navigate(-1); // grįžta į prieš tai buvusį puslapį
    };

    return (
        <Main
            className="edit-page"
            title={<div>Mokyklos redagavimas</div>}
            headerTitle={<div>Mokyklos: <span>{singleSchool?.name}</span> redagavimas</div>}
            menuWidth={350}
            menuPlacement="left"
            toolbar={toolbar}
            showFooter={false}
            floatingHeader={false}
            footerContent={<div />}
            floatingFooter
            backLink={false}
            breadcrumb={null}>
            <Button
                type="text"
                icon={<LeftOutlined />}
                onClick={handleBack}
                style={{ marginBottom: '16px' }}>
                Grįžti atgal
            </Button>
            <Tabs type="card" defaultActiveKey={defaultActiveKey}>
                <TabPane tab="Pagrindinis" key="main">
                    <Card>
                        <Form
                            layout="vertical"
                            form={form}
                            onFinish={onFinish}
                            // onFinishFailed={onFinishFailed}
                            initialValues={{
                                name: singleSchool?.name,
                                address: singleSchool?.address,
                                phone: singleSchool?.phone,
                                email: singleSchool?.email,
                                website: singleSchool?.website,
                                description: singleSchool?.description,
                                type: singleSchool?.type,
                                latitude: singleSchool?.latitude,
                                longitude: singleSchool?.longitude,
                            }}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        label="Pavadinimas"
                                        name="name"
                                        rules={[{ required: true, message: 'Įveskite mokyklos pavadinimą.' }]}>
                                        <Input placeholder="Įveskite mokyklos pavadinimą" />
                                    </Form.Item>

                                    <Form.Item
                                        label="Mokyklos tipas"
                                        name="type"
                                        rules={[{ required: true, message: 'Pasirinkite mokyklos tipą.' }]}>
                                        <Select
                                            placeholder="Pasirinkite mokyklos tipą"
                                            options={schoolTypeOptions}
                                            disabled={userData?.role_id === 5} />
                                    </Form.Item>

                                    <Form.Item
                                        label="Adresas"
                                        name="address"
                                        rules={[{ required: true, message: 'Įveskite mokyklos adresą.' }]}>
                                        <Input placeholder="Įveskite mokyklos adresą" />
                                    </Form.Item>

                                    <Divider />
                                    <h3>Koordinatės:</h3>
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Form.Item
                                                label="Platuma (latitude)"
                                                name="latitude"
                                                rules={[{ required: true, message: 'Įveskite platumą' }]}>
                                                <Input placeholder="Pvz. 55.123456" />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item
                                                label="Ilguma (longitude)"
                                                name="longitude"
                                                rules={[{ required: true, message: 'Įveskite ilgumą' }]}>
                                                <Input placeholder="Pvz. 23.123456" />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Divider />

                                    <Form.Item
                                        label="Telefono numeris"
                                        name="phone">
                                        <Input placeholder="Įveskite telefono numerį" />
                                    </Form.Item>

                                    <Form.Item
                                        label="El. paštas"
                                        name="email"
                                        rules={[{ type: 'email', message: 'Netinkamas el. pašto formatas' }]}>
                                        <Input placeholder="Įveskite el. paštą" />
                                    </Form.Item>

                                    <Form.Item
                                        label="Svetainė"
                                        name="website">
                                        <Input placeholder="Įveskite svetainės adresą" />
                                    </Form.Item>

                                    <Form.Item
                                        label="Aprašymas"
                                        name="description">
                                        <Input.TextArea placeholder="Įveskite mokyklos aprašymą" rows={4} />
                                    </Form.Item>

                                    <Form.Item>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            block
                                            loading={isFetching}>
                                            Atnaujinti mokyklą
                                        </Button>
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <SchoolMap
                                        lat={singleSchool?.latitude || 0}
                                        lng={singleSchool?.longitude || 0}
                                        name={singleSchool?.name}
                                        address={singleSchool?.address}
                                        phone={singleSchool?.phone}
                                        type={singleSchool?.type} />
                                    {/*
                                    <SchoolMap
                                        lat={singleSchool?.latitude || 0}
                                        lng={singleSchool?.longitude || 0}
                                        name={singleSchool?.name}
                                        address={singleSchool?.address}
                                        phone={singleSchool?.phone}
                                        type={singleSchool?.type} />
                                    */}
                                </Col>
                            </Row>
                        </Form>
                    </Card>
                </TabPane>
                <TabPane tab="Kontaktai" key="contacts">
                    <SchoolContacts />
                </TabPane>
            </Tabs>
        </Main>
    );
};

export default SchoolEdit;

SchoolEdit.propTypes = {};
